import React, {useState, useEffect, useRef} from 'react';
import { API_BASE, JITSI_HOST } from '../shared';

export function GoLiveButton({ agentId }) {
  const [liveRoom,   setLiveRoom]   = useState(null);
  const [copied,     setCopied]     = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [callEnded,  setCallEnded]  = useState(false);

  const jitsiContainerRef = useRef(null);
  const jitsiApiRef       = useRef(null);

  const startLive = () => {
    const room = `ogm-live-${agentId}-${Date.now()}`;
    setLiveRoom(room);
    setCallActive(true);
  };

  const stopLive = () => {
    jitsiApiRef.current?.dispose();
    setCallActive(false);
    setCallEnded(false);
    setLiveRoom(null);
    setCopied(false);
  };

  const copyLink = () => {
    const url = `${window.location.origin}/live/${liveRoom}`;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  useEffect(() => {
    if (!callActive || !liveRoom) return;

    const _JAAS   = process.env.REACT_APP_JAAS_APP_ID || '';
    const domain  = _JAAS ? '8x8.vc' : JITSI_HOST;
    const jaasRoom = _JAAS ? `${_JAAS}/${liveRoom}` : liveRoom;

    const initApi = async () => {
      if (!jitsiContainerRef.current) return;

      let jwt = null;
      if (_JAAS) {
        try {
          const r = await fetch(`${API_BASE}/api/live-tour/jaas-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('agent_token') || ''}` },
            body: JSON.stringify({ userName: 'Agent', roomName: liveRoom, moderator: 'true' }),
          });
          if (r.ok) jwt = (await r.json()).token;
        } catch { /* JWT fetch failure is non-critical — call still starts without moderator auth */ }
      }

      const opts = {
        roomName:   jaasRoom,
        parentNode: jitsiContainerRef.current,
        width:      '100%',
        height:     '100%',
        userInfo:   { displayName: 'Agent' },
        configOverwrite: {
          startWithAudioMuted:    false,
          startWithVideoMuted:    false,
          prejoinPageEnabled:     false,
          prejoinConfig:          { enabled: false },
          disableDeepLinking:     true,
          p2p:                    { enabled: false },
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK:             false,
          SHOW_BRAND_WATERMARK:             false,
          SHOW_POWERED_BY:                  false,
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
          TOOLBAR_ALWAYS_VISIBLE:           true,
          MOBILE_APP_PROMO:                 false,
        },
      };
      if (jwt) opts.jwt = jwt;

      jitsiApiRef.current = new window.JitsiMeetExternalAPI(domain, opts);
      jitsiApiRef.current.addEventListeners({
        readyToClose:    () => stopLive(),
        participantLeft: () => {
          const count = jitsiApiRef.current?.getNumberOfParticipants?.() || 0;
          if (count <= 1) setCallEnded(true);
        },
      });
    };

    const scriptSrc = _JAAS
        ? `https://8x8.vc/${_JAAS}/external_api.js`
        : `https://${JITSI_HOST}/external_api.js`;

    if (window.JitsiMeetExternalAPI) { initApi(); }
    else {
      const s  = document.createElement('script');
      s.src    = scriptSrc;
      s.async  = true;
      s.onload = initApi;
      document.body.appendChild(s);
    }

    return () => { jitsiApiRef.current?.dispose(); };
  }, [callActive, liveRoom]);

  if (callActive) {
    const shareUrl = `${window.location.origin}/live/${liveRoom}`;
    const isMobile = window.innerWidth < 600;
    return (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: '#111827', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            background: '#111827', display: 'flex', flexShrink: 0,
            alignItems: 'center', justifyContent: 'space-between',
            padding: isMobile ? '10px 14px' : '0 20px',
            height: isMobile ? 'auto' : 60,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? 8 : 0,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'linear-gradient(135deg,#3b82f6,#f97316)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 900, color: 'white', letterSpacing: '-0.3px',
                flexShrink: 0,
              }}>OGM</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ color: 'white', fontSize: 13, fontWeight: 800, lineHeight: 1 }}>OGM Live</span>
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, lineHeight: 1 }}>Agent Session</span>
              </div>
            </div>

            {isMobile ? (
                <div style={{
                  flex: '0 0 100%', order: 3,
                  display: 'flex', gap: 8,
                }}>
                  <button
                      onClick={copyLink}
                      style={{
                        flex: 1, height: 40, borderRadius: 10, border: 'none',
                        background: copied ? '#16a34a' : '#f97316',
                        color: 'white', fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', transition: 'all 0.18s',
                      }}
                  >
                    {copied ? '✓ Copied!' : '📋 Copy Link'}
                  </button>
                  <button
                      onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Join my live property tour: ' + shareUrl)}`, '_blank')}
                      style={{
                        flex: 1, height: 40, borderRadius: 10, border: 'none',
                        background: '#25D366', color: 'white',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                      }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.136.557 4.143 1.531 5.886L.05 23.95l6.264-1.464A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-1.937 0-3.745-.525-5.294-1.441l-.38-.226-3.718.869.934-3.621-.248-.393A9.795 9.795 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/>
                    </svg>
                    Share on WhatsApp
                  </button>
                </div>
            ) : (
                <div style={{
                  flex: 1, display: 'flex', alignItems: 'center',
                  gap: 8, justifyContent: 'center', padding: '0 16px',
                }}>
                  <div style={{
                    flex: 1, maxWidth: 340, display: 'flex', alignItems: 'center',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 10, overflow: 'hidden', height: 36,
                  }}>
                    <span style={{ padding: '0 10px', color: 'rgba(255,255,255,0.35)', fontSize: 12, flexShrink: 0 }}>🔗</span>
                    <span style={{
                      flex: 1, color: 'rgba(255,255,255,0.75)', fontSize: 11,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      paddingRight: 10,
                    }}>{shareUrl}</span>
                  </div>
                  <button
                      onClick={copyLink}
                      style={{
                        height: 36, padding: '0 18px', borderRadius: 10, border: 'none',
                        background: copied ? '#16a34a' : '#f97316',
                        color: 'white', fontSize: 12, fontWeight: 700,
                        cursor: 'pointer', transition: 'all 0.18s', flexShrink: 0,
                      }}
                  >
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                  <button
                      onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Join my live property tour: ' + shareUrl)}`, '_blank')}
                      style={{
                        height: 36, padding: '0 14px', borderRadius: 10, border: 'none',
                        background: '#25D366', color: 'white', fontSize: 12, fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                        flexShrink: 0,
                      }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.136.557 4.143 1.531 5.886L.05 23.95l6.264-1.464A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-1.937 0-3.745-.525-5.294-1.441l-.38-.226-3.718.869.934-3.621-.248-.393A9.795 9.795 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/>
                    </svg>
                    WhatsApp
                  </button>
                </div>
            )}

            <button
                onClick={stopLive}
                style={{
                  padding: '8px 18px', borderRadius: 8, border: 'none',
                  background: 'rgba(220,38,38,0.8)', color: 'white',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                }}
            >
              End Session
            </button>
          </div>

          <div ref={jitsiContainerRef} style={{ flex: 1, overflow: 'hidden' }} />

          {callEnded && (
              <div style={{
                position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(15,23,42,0.9)', color: 'white', borderRadius: 12,
                padding: '14px 24px', fontSize: 14, zIndex: 10000,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span>Customer has left the call.</span>
                <button
                    onClick={stopLive}
                    style={{
                      padding: '6px 14px', borderRadius: 8, border: 'none',
                      background: '#f97316', color: 'white', fontSize: 12,
                      fontWeight: 700, cursor: 'pointer',
                    }}
                >End Session</button>
              </div>
          )}
        </div>
    );
  }

  return (
      <button className="go-live-btn" onClick={startLive} title="Start a live video session">
        <span className="go-live-dot" aria-hidden="true" />
        Go Live
      </button>
  );
}
